import { PLATFORM_BASE_URL } from "../platform/config";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import React from "react";
import {
  OktonDataGrid,
  OktonDataGridColumn,
  OktonPageHeader,
  OktonPanel,
  useOktonAuth,
  OktonInput,
  OktonSelect,
  OktonTextarea,
} from "@okton/platform-ui";

interface GroupRow {
  id: string;
  name: string;
  description?: string;
  active: boolean;
  sort_order: number;
  package_count: number;
}

interface PackageRow {
  id: string;
  group_id: string;
  group_name: string;
  name: string;
  description?: string;
  active: boolean;
  sort_order: number;
  plan_count: number;
}

interface PlanRow {
  code: number;
  name: string;
  billing_type: string;
  active: boolean;
}

export const CommercialPage: React.FC = () => {
  const { accessToken } = useOktonAuth();
  const [groups, setGroups] = React.useState<GroupRow[]>([]);
  const [packages, setPackages] = React.useState<PackageRow[]>([]);
  const [plans, setPlans] = React.useState<PlanRow[]>([]);
  const [groupDetail, setGroupDetail] = React.useState<Record<
    string,
    any
  > | null>(null);
  const [packageDetail, setPackageDetail] = React.useState<Record<
    string,
    any
  > | null>(null);
  const [selectedPlanCodes, setSelectedPlanCodes] = React.useState<number[]>(
    [],
  );
  const [planSearch, setPlanSearch] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const groupModal = useDisclosure();
  const packageModal = useDisclosure();
  const borderColor = useColorModeValue("gray.200", "gray.700");

  const authHeaders = React.useMemo(
    () => ({
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    }),
    [accessToken],
  );

  const load = React.useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError("");
    try {
      const [groupsRes, packagesRes, plansRes] = await Promise.all([
        fetch(`${PLATFORM_BASE_URL}/v1/admin/commercial/groups`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: "include",
        }),
        fetch(`${PLATFORM_BASE_URL}/v1/admin/commercial/packages`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: "include",
        }),
        fetch(`${PLATFORM_BASE_URL}/v1/admin/catalog/plans`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: "include",
        }),
      ]);
      const [groupsPayload, packagesPayload, plansPayload] = await Promise.all([
        groupsRes.json(),
        packagesRes.json(),
        plansRes.json(),
      ]);
      if (!groupsRes.ok)
        throw new Error(
          groupsPayload.message || "Não foi possível carregar os grupos.",
        );
      if (!packagesRes.ok)
        throw new Error(
          packagesPayload.message || "Não foi possível carregar os pacotes.",
        );
      if (!plansRes.ok)
        throw new Error(
          plansPayload.message || "Não foi possível carregar os planos.",
        );
      setGroups(groupsPayload.data || []);
      setPackages(packagesPayload.data || []);
      setPlans(plansPayload.data || []);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Erro ao carregar catálogo comercial.",
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  React.useEffect(() => {
    load();
  }, [load]);

  function newGroup(): void {
    setGroupDetail({ name: "", description: "", active: true, sort_order: 0 });
    groupModal.onOpen();
  }

  async function openGroup(id: string): Promise<void> {
    if (!accessToken) return;
    const response = await fetch(
      `${PLATFORM_BASE_URL}/v1/admin/commercial/groups/${id}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: "include",
      },
    );
    const payload = await response.json();
    if (!response.ok) {
      setError(payload.message || "Não foi possível abrir o grupo.");
      return;
    }
    setGroupDetail(payload.data.group);
    groupModal.onOpen();
  }

  async function saveGroup(): Promise<void> {
    if (!accessToken || !groupDetail) return;
    setSaving(true);
    setError("");
    try {
      const id = groupDetail.id;
      const response = await fetch(
        id
          ? `${PLATFORM_BASE_URL}/v1/admin/commercial/groups/${id}`
          : `${PLATFORM_BASE_URL}/v1/admin/commercial/groups`,
        {
          method: id ? "PUT" : "POST",
          headers: authHeaders,
          credentials: "include",
          body: JSON.stringify(groupDetail),
        },
      );
      const payload = await response.json();
      if (!response.ok)
        throw new Error(payload.message || "Não foi possível salvar o grupo.");
      groupModal.onClose();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar grupo.");
    } finally {
      setSaving(false);
    }
  }

  function newPackage(): void {
    setPackageDetail({
      name: "",
      description: "",
      active: true,
      sort_order: 0,
      group_id: groups[0]?.id || "",
    });
    setSelectedPlanCodes([]);
    setPlanSearch("");
    packageModal.onOpen();
  }

  async function openPackage(id: string): Promise<void> {
    if (!accessToken) return;
    const response = await fetch(
      `${PLATFORM_BASE_URL}/v1/admin/commercial/packages/${id}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: "include",
      },
    );
    const payload = await response.json();
    if (!response.ok) {
      setError(payload.message || "Não foi possível abrir o pacote.");
      return;
    }
    setPackageDetail(payload.data.package);
    setSelectedPlanCodes(
      (payload.data.plans || []).map((plan: any) => Number(plan.code)),
    );
    setPlanSearch("");
    packageModal.onOpen();
  }

  function togglePlan(code: number, checked: boolean): void {
    setSelectedPlanCodes((current) => {
      if (checked) return Array.from(new Set([...current, code]));
      return current.filter((item) => item !== code);
    });
  }

  async function savePackage(): Promise<void> {
    if (!accessToken || !packageDetail) return;
    setSaving(true);
    setError("");
    try {
      const id = packageDetail.id;
      const response = await fetch(
        id
          ? `${PLATFORM_BASE_URL}/v1/admin/commercial/packages/${id}`
          : `${PLATFORM_BASE_URL}/v1/admin/commercial/packages`,
        {
          method: id ? "PUT" : "POST",
          headers: authHeaders,
          credentials: "include",
          body: JSON.stringify(packageDetail),
        },
      );
      const payload = await response.json();
      if (!response.ok)
        throw new Error(payload.message || "Não foi possível salvar o pacote.");

      const packageId = payload.data?.package?.id || id;
      const plansResponse = await fetch(
        `${PLATFORM_BASE_URL}/v1/admin/commercial/packages/${packageId}/plans`,
        {
          method: "PUT",
          headers: authHeaders,
          credentials: "include",
          body: JSON.stringify({ planCodes: selectedPlanCodes }),
        },
      );
      const plansPayload = await plansResponse.json();
      if (!plansResponse.ok)
        throw new Error(
          plansPayload.message ||
            "Não foi possível salvar os planos do pacote.",
        );

      packageModal.onClose();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar pacote.");
    } finally {
      setSaving(false);
    }
  }

  const filteredPlans = plans.filter((plan) => {
    const q = planSearch.trim().toLowerCase();
    if (!q) return true;
    return String(plan.code).includes(q) || plan.name.toLowerCase().includes(q);
  });

  const groupColumns = React.useMemo<OktonDataGridColumn<GroupRow>[]>(
    () => [
      {
        key: "group",
        header: "Grupo",
        render: (group) => (
          <Box>
            <Text fontWeight="600">{group.name}</Text>
            {group.description ? (
              <Text fontSize="xs" color="gray.500">
                {group.description}
              </Text>
            ) : null}
          </Box>
        ),
      },
      {
        key: "packages",
        header: "Pacotes",
        value: (group) => group.package_count,
      },
      {
        key: "status",
        header: "Status",
        render: (group) => (
          <Badge colorScheme={group.active ? "green" : "gray"}>
            {group.active ? "Ativo" : "Inativo"}
          </Badge>
        ),
      },
      {
        key: "actions",
        header: "",
        render: (group) => (
          <Button size="xs" onClick={() => openGroup(group.id)}>
            Editar
          </Button>
        ),
      },
    ],
    [],
  );

  const packageColumns = React.useMemo<OktonDataGridColumn<PackageRow>[]>(
    () => [
      {
        key: "package",
        header: "Pacote",
        render: (pkg) => <Text fontWeight="600">{pkg.name}</Text>,
      },
      { key: "group", header: "Grupo", value: (pkg) => pkg.group_name },
      { key: "plans", header: "Planos", value: (pkg) => pkg.plan_count },
      {
        key: "status",
        header: "Status",
        render: (pkg) => (
          <Badge colorScheme={pkg.active ? "green" : "gray"}>
            {pkg.active ? "Ativo" : "Inativo"}
          </Badge>
        ),
      },
      {
        key: "actions",
        header: "",
        render: (pkg) => (
          <Button
            size="xs"
            colorScheme="blue"
            onClick={() => openPackage(pkg.id)}
          >
            Editar
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <Box px={[4, 6, 9]} py={[6, 8, 16]} w="100%">
      <OktonPageHeader
        title="Grupos e Pacotes"
        subtitle="Organização comercial para facilitar vendas. O acesso real continua sendo controlado pelos Planos."
        actions={
          <Flex gap="3" wrap="wrap">
            <Button variant="outline" onClick={newGroup}>
              Novo grupo
            </Button>
            <Button
              colorScheme="blue"
              onClick={newPackage}
              isDisabled={groups.length === 0}
            >
              Novo pacote
            </Button>
          </Flex>
        }
      />

      <SimpleGrid columns={[1, 2]} spacing="6">
        <OktonPanel
          title="Grupos"
          subtitle="Categorias comerciais para organizar os pacotes."
          bodyStyle={{ padding: 0, overflowX: "auto" }}
        >
          <OktonDataGrid
            rows={groups}
            columns={groupColumns}
            getRowId={(group) => group.id}
            loading={loading}
            emptyMessage="Nenhum grupo cadastrado. Crie um grupo para organizar os pacotes comerciais."
            hidePagination
          />
        </OktonPanel>

        <OktonPanel
          title="Pacotes"
          subtitle="Modelos comerciais compostos por Planos."
          bodyStyle={{ padding: 0, overflowX: "auto" }}
        >
          <OktonDataGrid
            rows={packages}
            columns={packageColumns}
            getRowId={(pkg) => pkg.id}
            loading={loading}
            emptyMessage="Nenhum pacote cadastrado. Crie um pacote para combinar planos em uma oferta comercial."
            hidePagination
          />
        </OktonPanel>
      </SimpleGrid>

      {error && (
        <Text mt="4" color="red.500">
          {error}
        </Text>
      )}

      <Modal isOpen={groupModal.isOpen} onClose={groupModal.onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {groupDetail?.id
              ? `Editar grupo ${groupDetail.name}`
              : "Novo grupo"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {groupDetail && (
              <Stack spacing="4">
                <OktonInput
                  name="commercial-group-name"
                  label="Nome"
                  value={groupDetail.name || ""}
                  onChange={(e) =>
                    setGroupDetail((current) =>
                      current ? { ...current, name: e.target.value } : current,
                    )
                  }
                />
                <OktonTextarea
                  name="commercial-group-description"
                  label="Descrição"
                  value={groupDetail.description || ""}
                  onChange={(e) =>
                    setGroupDetail((current) =>
                      current
                        ? { ...current, description: e.target.value }
                        : current,
                    )
                  }
                />
                <OktonInput
                  name="commercial-group-order"
                  label="Ordem"
                  type="number"
                  value={groupDetail.sort_order ?? 0}
                  onChange={(e) =>
                    setGroupDetail((current) =>
                      current
                        ? { ...current, sort_order: Number(e.target.value) }
                        : current,
                    )
                  }
                />
                <Checkbox
                  isChecked={groupDetail.active !== false}
                  onChange={(e) =>
                    setGroupDetail((current) =>
                      current
                        ? { ...current, active: e.target.checked }
                        : current,
                    )
                  }
                >
                  Grupo ativo
                </Checkbox>
              </Stack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr="3" onClick={groupModal.onClose}>
              Cancelar
            </Button>
            <Button colorScheme="blue" isLoading={saving} onClick={saveGroup}>
              Salvar grupo
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={packageModal.isOpen}
        onClose={packageModal.onClose}
        size="5xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {packageDetail?.id
              ? `Editar pacote ${packageDetail.name}`
              : "Novo pacote"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {packageDetail && (
              <>
                <SimpleGrid columns={[1, 2]} spacing="4">
                  <OktonInput
                    name="commercial-package-name"
                    label="Nome"
                    value={packageDetail.name || ""}
                    onChange={(e) =>
                      setPackageDetail((current) =>
                        current
                          ? { ...current, name: e.target.value }
                          : current,
                      )
                    }
                  />
                  <OktonSelect
                    name="commercial-package-group"
                    label="Grupo"
                    value={packageDetail.group_id || ""}
                    onChange={(e) =>
                      setPackageDetail((current) =>
                        current
                          ? { ...current, group_id: e.target.value }
                          : current,
                      )
                    }
                  >
                    {groups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </OktonSelect>
                  <Box gridColumn={[null, "1 / span 2"]}>
                    <OktonTextarea
                      name="commercial-package-description"
                      label="Descrição"
                      value={packageDetail.description || ""}
                      onChange={(e) =>
                        setPackageDetail((current) =>
                          current
                            ? { ...current, description: e.target.value }
                            : current,
                        )
                      }
                    />
                  </Box>
                  <OktonInput
                    name="commercial-package-order"
                    label="Ordem"
                    type="number"
                    value={packageDetail.sort_order ?? 0}
                    onChange={(e) =>
                      setPackageDetail((current) =>
                        current
                          ? { ...current, sort_order: Number(e.target.value) }
                          : current,
                      )
                    }
                  />
                  <Flex align="flex-end">
                    <Checkbox
                      isChecked={packageDetail.active !== false}
                      onChange={(e) =>
                        setPackageDetail((current) =>
                          current
                            ? { ...current, active: e.target.checked }
                            : current,
                        )
                      }
                    >
                      Pacote ativo
                    </Checkbox>
                  </Flex>
                </SimpleGrid>

                <Heading size="sm" mt="7" mb="3">
                  Planos que compõem o pacote
                </Heading>
                <OktonInput
                  name="commercial-plan-search"
                  placeholder="Buscar por código ou nome do plano"
                  value={planSearch}
                  onChange={(e) => setPlanSearch(e.target.value)}
                  mb="3"
                />
                <Box
                  borderWidth="1px"
                  borderColor={borderColor}
                  rounded="md"
                  maxH="360px"
                  overflowY="auto"
                >
                  <Stack spacing="0">
                    {filteredPlans.map((plan) => (
                      <Flex
                        key={plan.code}
                        px="4"
                        py="3"
                        align="center"
                        justify="space-between"
                        borderBottomWidth="1px"
                        borderColor={borderColor}
                      >
                        <Checkbox
                          isChecked={selectedPlanCodes.includes(
                            Number(plan.code),
                          )}
                          onChange={(e) =>
                            togglePlan(Number(plan.code), e.target.checked)
                          }
                        >
                          <Text fontWeight="600">
                            {plan.code} - {plan.name}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            Cobrança: {plan.billing_type}
                          </Text>
                        </Checkbox>
                        <Badge colorScheme={plan.active ? "green" : "gray"}>
                          {plan.active ? "Ativo" : "Inativo"}
                        </Badge>
                      </Flex>
                    ))}
                  </Stack>
                </Box>
                <Text mt="3" fontSize="sm" color="gray.500">
                  {selectedPlanCodes.length} plano
                  {selectedPlanCodes.length === 1 ? "" : "s"} selecionado
                  {selectedPlanCodes.length === 1 ? "" : "s"}.
                </Text>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr="3" onClick={packageModal.onClose}>
              Cancelar
            </Button>
            <Button colorScheme="blue" isLoading={saving} onClick={savePackage}>
              Salvar pacote
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};
